import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Anchor, MessageSquare, Library, FileDigit, ShieldOff,
  Lock, ArrowRight, Sparkles, ScrollText, Send as SendIcon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getMembership } from "../lib/api";

export default function OperatorWelcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isArchitect = !!user?.is_architect;

  // Resolve the caller's tier so we can route them correctly. Console is
  // sealed to Builders + Architect; free operators land on the public
  // truth feed where they can actually earn AEGIS.
  const [tier, setTier] = useState(isArchitect ? "architect" : null);
  useEffect(() => {
    if (isArchitect) {
      setTier("architect");
      return;
    }
    let cancelled = false;
    getMembership()
      .then((m) => { if (!cancelled) setTier(m?.tier || "free"); })
      .catch(() => { if (!cancelled) setTier("free"); });
    return () => { cancelled = true; };
  }, [isArchitect]);

  const consoleUnlocked = tier === "architect" || tier === "builder";

  const enter = () => {
    if (user?.user_id) {
      localStorage.setItem(`projectivy.welcome_seen.${user.user_id}`, "1");
    }
    // v19.18 · Every tier lands on the Submit surface (/submit) post-login.
    // Console + Ask + Truths remain one nav hop away.
    navigate("/submit");
  };

  const allowed = consoleUnlocked
    ? [
        {
          icon: MessageSquare,
          title: "Chat with the Sentinel",
          body: "Every message passes through the 8-layer protocol before it reaches you. Your conversations are private to your workspace.",
        },
        {
          icon: Library,
          title: "Your Architect's Archive",
          body: "Upload documents. The Sentinel grounds its replies against your archive first — your facts, your truth tether.",
        },
        {
          icon: FileDigit,
          title: "Your docking receipts",
          body: "Every 2,500 tokens the Humanitarian Pulse mints an HMAC-signed receipt of your session. View them on the Bio page.",
        },
      ]
    : [
        {
          icon: ScrollText,
          title: "Verify the public truth feed",
          body: "Open /truths. Every claim is auto-classified into Fact, Fiction, or Bounty. Confirm or dispute one and you mint AEGIS — this is how operators earn.",
        },
        {
          icon: SendIcon,
          title: "Submit your own claims",
          body: "Use /submit to push a claim with sources into the triangulation engine. Solid claims mint you AEGIS on confirmation.",
        },
        {
          icon: MessageSquare,
          title: "Ask the Architect",
          body: "Use the Ask board to put questions to the network. The Sentinel chat console is reserved for Builders & Architects — it unlocks later as you climb the verifier ladder.",
        },
      ];

  const sealed = consoleUnlocked
    ? [
        { label: "The Black Box Vault" },
        { label: "System prompt + strictness edits" },
        { label: "Layer 2 · Architect Audit" },
        { label: "Safe Mode release" },
        { label: "Outbound Multi-Model Handshakes" },
      ]
    : [
        { label: "Sentinel Console · direct chat" },
        { label: "Orion · vision rendering" },
        { label: "Ledger · raw citation lineage" },
        { label: "Replay · session playback" },
        { label: "The Black Box Vault" },
      ];

  return (
    <div
      className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-16 bg-[#09090b]"
      data-testid="operator-welcome-page"
    >
      <div className="max-w-4xl w-full">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-emerald-400">
          <Anchor className="h-3 w-3" />
          Welcome, Operator
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl mt-3">
          You are inside the Sentinel.
        </h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          {consoleUnlocked ? (
            <>
              Projectivy Sentinel v18.48 is an AI middleware running the HSL /
              Obsidian Sink protocol. It sits between you and a Gemini 3 model,
              passing every message through 8 safety and resonance layers before
              a reply reaches you. Here is what your role grants you and what
              remains sealed.
            </>
          ) : (
            <>
              The Sentinel Console is unlocked later — once you&apos;ve earned standing
              in the verifier network. For now, you operate on the public truth
              floor: check claims, dispute fiction, submit your own. This is how
              AEGIS is actually minted. Here&apos;s what&apos;s open to you, and what&apos;s still
              sealed.
            </>
          )}
        </p>

        {/* What you can do */}
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">
              What you can do
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {allowed.map((a) => (
              <div
                key={a.title}
                className="border border-zinc-800 bg-[#0b0b0d] p-5"
                data-testid={`welcome-allowed-${a.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <a.icon className="h-4 w-4 text-emerald-400" />
                <div className="mt-3 font-heading text-lg">{a.title}</div>
                <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                  {a.body}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What is sealed */}
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="h-3.5 w-3.5 text-zinc-500" />
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {consoleUnlocked ? "Sealed to the Architect" : "Unlocks as you climb"}
            </h2>
          </div>
          <div className="border border-zinc-900 bg-[#09090b]">
            {sealed.map((s, i, arr) => (
              <div
                key={s.label}
                className={`flex items-center gap-3 px-5 py-3 text-xs text-zinc-500 ${
                  i !== arr.length - 1 ? "border-b border-zinc-900" : ""
                }`}
              >
                <ShieldOff className="h-3 w-3" />
                {s.label}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-600 mt-3 leading-relaxed max-w-2xl">
            {consoleUnlocked
              ? "These controls shape the Sentinel's behavior for every user, so they are locked to the Architect's email."
              : "These features open up as you earn standing — verify truths, submit good claims, and your account climbs to Builder tier automatically. No architect approval needed; the chain does the gating."}
          </div>
        </section>

        <div className="mt-12 flex items-center gap-4 flex-wrap">
          <button
            onClick={enter}
            data-testid="welcome-enter-btn"
            className="inline-flex items-center gap-2 px-8 py-4 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.3em]"
          >
            {consoleUnlocked ? "Enter the Console" : "Open the Truth Feed"}
            <ArrowRight className="h-4 w-4" />
          </button>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            {consoleUnlocked
              ? "You will not see this again."
              : "Console unlocks at Builder tier · keep verifying."}
          </div>
        </div>
      </div>
    </div>
  );
}
